// Search (/search?q=): producers and seeds. The query lives in the URL so a
// search can be shared and survives the back button.

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import type { SearchResult } from "@shared/api-types.js";
import { Input } from "@web-shared/components/ui/input";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { webApi } from "@web-shared/lib/web-api";
import { profilePath } from "@/lib/ctas";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Avatar } from "../components/Avatar";
import { EmptyState } from "../components/CardList";
import { FeedCardView, feedTrack } from "../components/FeedCardView";

const DEBOUNCE_MS = 250;

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  useDocumentTitle("Search");
  const q = params.get("q") ?? "";
  const [draft, setDraft] = useState(q);

  // Type freely; the URL (and the request) follow once typing pauses.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (draft.trim() !== q) setParams(draft.trim() ? { q: draft.trim() } : {}, { replace: true });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [draft, q, setParams]);

  const result = useQuery({
    queryKey: ["web", "search", q.toLowerCase()],
    enabled: q.length >= 2,
    staleTime: 5 * 60_000,
    queryFn: () => webApi<SearchResult>(`/search?q=${encodeURIComponent(q)}`),
  });
  const queue = useMemo(() => (result.data?.seeds ?? []).filter((c) => c.audioUrl !== "").map((c) => feedTrack(c)), [result.data]);

  return (
    <div className="space-y-5">
      <h1 className="text-3xl font-black tracking-tight">Search</h1>
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search seeds, genres, producers"
          aria-label="Search"
          className="pl-9"
        />
      </label>

      {q.length < 2 ? (
        <p className="text-center text-sm text-muted-foreground">Find a seed by its name or genre, or a producer by name.</p>
      ) : result.isPending ? (
        <Skeleton className="h-40 w-full rounded-2xl" />
      ) : result.isError ? (
        <EmptyState>
          <p className="text-removed">Search failed.</p>
        </EmptyState>
      ) : result.data.seeds.length === 0 && result.data.users.length === 0 ? (
        <EmptyState>
          <p>Nothing matches “{q}”.</p>
        </EmptyState>
      ) : (
        <>
          {result.data.users.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Producers</h2>
              <ul className="scrollbar-thin -mx-4 flex gap-3 overflow-x-auto px-4 pb-1">
                {result.data.users.map((u) => (
                  <li key={u.id} className="shrink-0">
                    <Link to={profilePath(u.handle)} className="flex w-20 flex-col items-center gap-1.5 text-center">
                      <Avatar name={u.username} url={u.avatarUrl} className="size-14 text-base" />
                      <span className="w-full truncate text-xs">{u.username}</span>
                      <span className="w-full truncate text-[10px] text-muted-foreground">@{u.handle}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {result.data.seeds.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Seeds</h2>
              {result.data.seeds.map((card) => (
                <FeedCardView key={card.id} card={card} queue={queue} />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
